import React from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Smartphone, Tablet, Apple, Play, ExternalLink, Copy, Check, ArrowRight, QrCode, ShoppingCart } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import SEOMeta, { BOOK_META } from '../../../components/SEOMeta';

const BOOK_SLUG = 'cultural-fluency-without-compromise';
const BASE_URL = 'https://thirdcultureleadership.com';
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01';

const BookAppsPage = () => {
  const [copiedLink, setCopiedLink] = React.useState(null);

  const platforms = [
    {
      id: 'web',
      title: 'Web Reader',
      description: 'Read instantly in your browser on any device. No download required.',
      icon: Monitor,
      status: 'available',
      cta: 'Open Web Reader',
      link: `/books/${BOOK_SLUG}/read`,
      features: ['Works on Mac, PC, Chromebook', 'Light, Dark & Sepia themes', 'Syncs progress automatically']
    },
    {
      id: 'ios',
      title: 'iPhone & iPad',
      description: 'Native iOS app with offline reading and beautiful typography.',
      icon: Apple,
      status: 'coming-soon',
      cta: 'View iOS Page',
      link: `/books/${BOOK_SLUG}/ios`,
      features: ['Offline reading', 'Apple Books integration', 'iPad multitasking support']
    },
    {
      id: 'android',
      title: 'Android & Tablet',
      description: 'Full-featured Android app for phones and tablets.',
      icon: Play,
      status: 'coming-soon',
      cta: 'View Android Page',
      link: `/books/${BOOK_SLUG}/android`,
      features: ['Material Design', 'Google Play Books integration', 'Tablet-optimized layout']
    }
  ];

  const links = [
    { label: 'Web Reader', url: `${BASE_URL}/books/${BOOK_SLUG}/read` },
    { label: 'Apps', url: `${BASE_URL}/books/${BOOK_SLUG}/apps` },
    { label: 'iOS', url: `${BASE_URL}/books/${BOOK_SLUG}/ios` },
    { label: 'Android', url: `${BASE_URL}/books/${BOOK_SLUG}/android` },
    { label: 'Library', url: `${BASE_URL}/books/${BOOK_SLUG}/library` },
  ];

  const copyToClipboard = (url, label) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(label);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <SEOMeta 
        url="/books/cultural-fluency-without-compromise/apps"
        title="Read Anywhere — Cultural Fluency Without Compromise"
        description="Access Cultural Fluency Without Compromise on web, iOS, and Android. Your progress syncs automatically across all devices."
        image={BOOK_META.image}
        imageAlt={BOOK_META.imageAlt}
      />
      {/* Header */}
      <header className="bg-gradient-to-br from-[#2c3e50] to-[#1a252f] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Smartphone className="w-12 h-12 mx-auto mb-4 text-[#d4af37]" />
          <h1 className="text-3xl md:text-4xl font-bold mb-4 font-display">Read Anywhere</h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto">
            Access &ldquo;Cultural Fluency Without Compromise&rdquo; on any device. Your progress syncs automatically.
          </p>
        </div>
      </header>

      {/* Platform Cards */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {platforms.map((platform) => (
              <div
                key={platform.id}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#d4af37]/10 rounded-xl flex items-center justify-center">
                    <platform.icon className="w-6 h-6 text-[#d4af37]" />
                  </div>
                  {platform.status === 'coming-soon' && (
                    <span className="text-xs bg-[#2c3e50]/10 text-[#2c3e50] px-2 py-1 rounded-full">
                      Coming Soon
                    </span>
                  )}
                  {platform.status === 'available' && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      Available Now
                    </span>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-[#2c3e50] mb-2">{platform.title}</h3>
                <p className="text-[#5a6c7d] text-sm mb-4">{platform.description}</p>
                
                <ul className="space-y-2 mb-6">
                  {platform.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[#5a6c7d]">
                      <div className="w-1.5 h-1.5 bg-[#d4af37] rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Link
                  to={platform.link}
                  className={`flex items-center justify-center gap-2 w-full py-3 rounded-lg font-medium transition-colors ${
                    platform.status === 'available'
                      ? 'bg-[#d4af37] text-[#2c3e50] hover:bg-[#e5c349]'
                      : 'bg-[#2c3e50]/10 text-[#2c3e50] hover:bg-[#2c3e50]/20'
                  }`}
                  data-testid={`platform-${platform.id}-btn`}
                >
                  {platform.cta}
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mac Note */}
      <section className="py-8 px-4 bg-white/50">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-[#5a6c7d] mb-2">
            <Monitor size={18} />
            <span className="font-medium">Mac Access</span>
          </div>
          <p className="text-sm text-[#5a6c7d]">
            Use the Web Reader on your Mac for the best desktop experience. 
            The iPad app will also be available on Apple Silicon Macs when released.
          </p>
        </div>
      </section>

      {/* Scan to Access - QR Codes */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <QrCode className="w-10 h-10 mx-auto mb-4 text-[#d4af37]" />
            <h2 className="text-2xl font-bold text-[#2c3e50] font-display">Scan to Access</h2>
            <p className="text-[#5a6c7d] mt-2">Use your phone camera to scan any QR code below</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Web Reader QR */}
            <div className="bg-[#faf8f5] rounded-2xl p-6 text-center">
              <div className="bg-white p-4 rounded-xl inline-block mb-4 shadow-sm">
                <QRCodeSVG 
                  value={`${BASE_URL}/books/${BOOK_SLUG}/read`}
                  size={140}
                  level="M"
                  fgColor="#2c3e50"
                />
              </div>
              <h3 className="font-semibold text-[#2c3e50] mb-1">Web Reader</h3>
              <p className="text-xs text-[#5a6c7d] mb-3">Scan to open reader</p>
              <button
                onClick={() => copyToClipboard(`${BASE_URL}/books/${BOOK_SLUG}/read`, 'qr-web')}
                className="inline-flex items-center gap-2 text-sm text-[#d4af37] hover:text-[#b8962f] transition-colors"
              >
                {copiedLink === 'qr-web' ? <Check size={14} /> : <Copy size={14} />}
                {copiedLink === 'qr-web' ? 'Copied!' : 'Copy link'}
              </button>
            </div>

            {/* Apps Hub QR */}
            <div className="bg-[#faf8f5] rounded-2xl p-6 text-center">
              <div className="bg-white p-4 rounded-xl inline-block mb-4 shadow-sm">
                <QRCodeSVG 
                  value={`${BASE_URL}/books/${BOOK_SLUG}/apps`}
                  size={140}
                  level="M"
                  fgColor="#2c3e50"
                />
              </div>
              <h3 className="font-semibold text-[#2c3e50] mb-1">Apps Hub</h3>
              <p className="text-xs text-[#5a6c7d] mb-3">Scan to view all platforms</p>
              <button
                onClick={() => copyToClipboard(`${BASE_URL}/books/${BOOK_SLUG}/apps`, 'qr-apps')}
                className="inline-flex items-center gap-2 text-sm text-[#d4af37] hover:text-[#b8962f] transition-colors"
              >
                {copiedLink === 'qr-apps' ? <Check size={14} /> : <Copy size={14} />}
                {copiedLink === 'qr-apps' ? 'Copied!' : 'Copy link'}
              </button>
            </div>

            {/* iOS QR */}
            <div className="bg-[#faf8f5] rounded-2xl p-6 text-center">
              <div className="bg-white p-4 rounded-xl inline-block mb-4 shadow-sm">
                <QRCodeSVG 
                  value={`${BASE_URL}/books/${BOOK_SLUG}/ios`}
                  size={140}
                  level="M"
                  fgColor="#2c3e50"
                />
              </div>
              <h3 className="font-semibold text-[#2c3e50] mb-1">iOS Page</h3>
              <p className="text-xs text-[#5a6c7d] mb-3">Scan for iPhone & iPad</p>
              <button
                onClick={() => copyToClipboard(`${BASE_URL}/books/${BOOK_SLUG}/ios`, 'qr-ios')}
                className="inline-flex items-center gap-2 text-sm text-[#d4af37] hover:text-[#b8962f] transition-colors"
              >
                {copiedLink === 'qr-ios' ? <Check size={14} /> : <Copy size={14} />}
                {copiedLink === 'qr-ios' ? 'Copied!' : 'Copy link'}
              </button>
            </div>

            {/* Android QR */}
            <div className="bg-[#faf8f5] rounded-2xl p-6 text-center">
              <div className="bg-white p-4 rounded-xl inline-block mb-4 shadow-sm">
                <QRCodeSVG 
                  value={`${BASE_URL}/books/${BOOK_SLUG}/android`}
                  size={140}
                  level="M"
                  fgColor="#2c3e50"
                />
              </div>
              <h3 className="font-semibold text-[#2c3e50] mb-1">Android Page</h3>
              <p className="text-xs text-[#5a6c7d] mb-3">Scan for Android devices</p>
              <button
                onClick={() => copyToClipboard(`${BASE_URL}/books/${BOOK_SLUG}/android`, 'qr-android')}
                className="inline-flex items-center gap-2 text-sm text-[#d4af37] hover:text-[#b8962f] transition-colors"
              >
                {copiedLink === 'qr-android' ? <Check size={14} /> : <Copy size={14} />}
                {copiedLink === 'qr-android' ? 'Copied!' : 'Copy link'}
              </button>
            </div>

            {/* Buy eBook QR */}
            <div className="bg-gradient-to-br from-[#2c3e50] to-[#1a252f] rounded-2xl p-6 text-center sm:col-span-2 lg:col-span-1">
              <div className="bg-white p-4 rounded-xl inline-block mb-4 shadow-sm">
                <QRCodeSVG 
                  value={STRIPE_PAYMENT_LINK}
                  size={140}
                  level="M"
                  fgColor="#d4af37"
                />
              </div>
              <h3 className="font-semibold text-white mb-1 flex items-center justify-center gap-2">
                <ShoppingCart size={16} />
                Buy eBook Access
              </h3>
              <p className="text-xs text-white/70 mb-3">Scan to purchase – £9.99</p>
              <button
                onClick={() => copyToClipboard(STRIPE_PAYMENT_LINK, 'qr-buy')}
                className="inline-flex items-center gap-2 text-sm text-[#d4af37] hover:text-[#e5c349] transition-colors"
              >
                {copiedLink === 'qr-buy' ? <Check size={14} /> : <Copy size={14} />}
                {copiedLink === 'qr-buy' ? 'Copied!' : 'Copy link'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Links Section */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-[#2c3e50] text-center mb-8 font-display">
            Quick Links
          </h2>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {links.map((link, index) => (
              <div 
                key={link.label}
                className={`flex items-center justify-between p-4 ${
                  index !== links.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#2c3e50]">{link.label}</p>
                  <p className="text-xs text-[#5a6c7d] truncate">{link.url}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyToClipboard(link.url, link.label)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copy link"
                  >
                    {copiedLink === link.label ? (
                      <Check size={16} className="text-green-600" />
                    ) : (
                      <Copy size={16} className="text-[#5a6c7d]" />
                    )}
                  </button>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Open link"
                  >
                    <ExternalLink size={16} className="text-[#5a6c7d]" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2c3e50] text-white py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link 
            to={`/books/${BOOK_SLUG}`}
            className="text-[#d4af37] hover:underline"
          >
            ← Back to Book
          </Link>
          <p className="text-sm text-white/50 mt-4">
            © {new Date().getFullYear()} Third Culture Leadership. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookAppsPage;
